import { forwardRef, useMemo, useState } from 'react';

import StarProgress from '@/features/gameSession/common/components/StarProgress';

import WhiteMilepost from '@/features/gameSession/common/assets/white-milepost.svg?react';

import type { RefObject } from 'react';

type MilepostProgressProps = {
	title?: string;
	levelImage?: string;
	levelName?: string;
	levelCompleted?: {
		level_name?: string | null;
		level_title?: string;
		level_image?: string;
	};
	achievementRef?: RefObject<HTMLElement | null>;
	starProgress: {
		total: number;
		earned: number;
		previousEarned?: number;
		withAnimation?: boolean;
		showAnimation?: boolean;
		onAnimationComplete?: () => void;
		tooltipOpen?: boolean;
	};
	className?: string;
};

const MilepostProgress = forwardRef<HTMLDivElement, MilepostProgressProps>(
	({ title, levelImage, levelName, levelCompleted, achievementRef, starProgress, className }, ref) => {
		const [isTooltipVisible, setIsTooltipVisible] = useState(false);

		const tooltipOpen = useMemo(
			() => starProgress.tooltipOpen ?? isTooltipVisible,
			[isTooltipVisible, starProgress.tooltipOpen],
		);
		const milepostClassName = useMemo(
			() => ['active-challenge__milepost', className].filter(Boolean).join(' '),
			[className],
		);
		const completedLevelName = levelCompleted?.level_name ?? levelCompleted?.level_title;
		const completedLevelImage = levelCompleted?.level_image;

		return (
			<div
				className={milepostClassName}
				ref={ref}
				onMouseEnter={() => setIsTooltipVisible(true)}
				onMouseLeave={() => setIsTooltipVisible(false)}
				onFocus={() => setIsTooltipVisible(true)}
				onBlur={() => setIsTooltipVisible(false)}
			>
				{title && <h3 className="active-challenge__milepost-title">{title}</h3>}
				{levelImage && (
					<img src={levelImage} alt={levelName || ''} className="active-challenge__milepost-achievement" />
				)}
				<WhiteMilepost className="active-challenge__milepost-image" />
				{levelCompleted && completedLevelName && (
					<div className="active-challenge__milepost-text">{completedLevelName}</div>
				)}
				{levelCompleted && completedLevelImage && (
					<figure className="active-challenge__achievement" ref={achievementRef}>
						<img
							src={completedLevelImage}
							alt={completedLevelName || ''}
							className="active-challenge__achievement-image"
						/>
					</figure>
				)}
				<StarProgress {...starProgress} tooltipOpen={tooltipOpen} />
			</div>
		);
	},
);

MilepostProgress.displayName = 'MilepostProgress';

export default MilepostProgress;
